import { Injectable, inject, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, firstValueFrom, catchError, tap, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrganizationContextService } from './organization-context.service';

/**
 * Onboarding Checklist Item
 */
export interface OnboardingChecklistItem {
  itemKey: string;
  title: string;
  description: string;
  isCompleted: boolean;
  isRequired: boolean;
  isVisible: boolean;
  itemOrder: number;
  completedAt?: Date | null;
  actionRoute?: string;
}

/**
 * API Response for owner onboarding progress
 */
interface OwnerOnboardingProgressResponse {
  success: boolean;
  data?: {
    checklist: OnboardingChecklistItem[];
    stats: {
      completed: number;
      total: number;
      percentComplete: number;
      allRequiredComplete: boolean;
    };
    dismissed: boolean;
    completedAt: string | null;
  };
  error?: string;
}

/**
 * @deprecated Replaced by UnifiedOnboardingService as of 2026-02-13.
 * See: agent-os/specs/2026-02-13-unified-onboarding-redesign/spec.md
 * Kept during transition period. Safe to remove when no other code references it.
 */
@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private readonly STORAGE_KEY_DISMISSED = 'adn_onboarding_dismissed';
  private readonly STORAGE_KEY_COMPLETED = 'adn_onboarding_completed';
  private readonly STORAGE_KEY_CHECKLIST = 'adn_onboarding_checklist';
  private readonly SESSION_KEY_WELCOME_SHOWN = 'adn_welcome_message_shown';

  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;
  private organizationContext = inject(OrganizationContextService);

  // Session-based tracking for welcome message (resets on page refresh/new tab)
  private welcomeMessageShownSubject: BehaviorSubject<boolean>;
  public welcomeMessageShown$: Observable<boolean>;

  // Default checklist items (used as fallback)
  private readonly defaultChecklist: OnboardingChecklistItem[] = [
    {
      itemKey: 'create_company',
      title: 'Create Company',
      description: 'Set up your company profile and information',
      isCompleted: false,
      isRequired: true,
      isVisible: true,
      itemOrder: 1,
      actionRoute: '/company-profile'
    },
    {
      itemKey: 'upload_certifications',
      title: 'Upload Certifications',
      description: 'Add your FAA certifications (Part 107, Part 137, 44807)',
      isCompleted: false,
      isRequired: true,
      isVisible: true,
      itemOrder: 2,
      actionRoute: '/certifications'
    },
    {
      itemKey: 'setup_billing',
      title: 'Set up Billing',
      description: 'Add payment method and billing information',
      isCompleted: false,
      isRequired: false, // Hidden until billing integration is implemented
      isVisible: false, // Hidden until billing integration is implemented
      itemOrder: 3,
      actionRoute: '/settings/billing'
    },
    {
      itemKey: 'invite_pilots',
      title: 'Invite Team Members',
      description: 'Invite pilots to join your organization (optional)',
      isCompleted: false,
      isRequired: false,
      isVisible: true,
      itemOrder: 4,
      actionRoute: '/team'
    },
    {
      itemKey: 'add_customers',
      title: 'Add Customers',
      description: 'Add your first customer to organize fields and records',
      isCompleted: false,
      isRequired: false,
      isVisible: true,
      itemOrder: 5,
      actionRoute: '/customers/new'
    }
  ];

  private checklistSubject: BehaviorSubject<OnboardingChecklistItem[]>;
  public checklist$: Observable<OnboardingChecklistItem[]>;

  private onboardingCompletedSubject: BehaviorSubject<boolean>;
  public onboardingCompleted$: Observable<boolean>;

  private onboardingDismissedSubject: BehaviorSubject<boolean>;
  public onboardingDismissed$: Observable<boolean>;

  constructor() {
    // Load persisted state from localStorage as initial state
    const dismissedState = this.loadFromStorage<boolean>(this.STORAGE_KEY_DISMISSED, false);
    const completedState = this.loadFromStorage<boolean>(this.STORAGE_KEY_COMPLETED, false);
    const checklistState = this.loadFromStorage<OnboardingChecklistItem[]>(this.STORAGE_KEY_CHECKLIST, this.defaultChecklist);

    // Load session-based welcome message state (resets on new session)
    const welcomeShownState = this.loadFromSessionStorage<boolean>(this.SESSION_KEY_WELCOME_SHOWN, false);

    // Initialize subjects with persisted state
    this.checklistSubject = new BehaviorSubject<OnboardingChecklistItem[]>(checklistState);
    this.checklist$ = this.checklistSubject.asObservable();

    this.onboardingCompletedSubject = new BehaviorSubject<boolean>(completedState);
    this.onboardingCompleted$ = this.onboardingCompletedSubject.asObservable();

    this.onboardingDismissedSubject = new BehaviorSubject<boolean>(dismissedState);
    this.onboardingDismissed$ = this.onboardingDismissedSubject.asObservable();

    this.welcomeMessageShownSubject = new BehaviorSubject<boolean>(welcomeShownState);
    this.welcomeMessageShown$ = this.welcomeMessageShownSubject.asObservable();

    // Set up effect to sync when organization context changes
    effect(() => {
      const orgId = this.organizationContext.currentOrganizationId();
      if (orgId) {
        // Fetch progress from API when organization context is set
        this.fetchProgressFromApi();
      }
    });
  }

  /**
   * Get the current organization ID from context service
   */
  getOrganizationId(): string | null {
    return this.organizationContext.getCurrentOrganizationId();
  }

  /**
   * Load data from localStorage
   */
  private loadFromStorage<T>(key: string, defaultValue: T): T {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
      return defaultValue;
    }
  }

  /**
   * Save data to localStorage
   */
  private saveToStorage<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
    }
  }

  /**
   * Load data from sessionStorage (resets on new session/tab)
   */
  private loadFromSessionStorage<T>(key: string, defaultValue: T): T {
    try {
      const stored = sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.error(`Error loading ${key} from sessionStorage:`, error);
      return defaultValue;
    }
  }

  /**
   * Save data to sessionStorage (resets on new session/tab)
   */
  private saveToSessionStorage<T>(key: string, value: T): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error saving ${key} to sessionStorage:`, error);
    }
  }

  /**
   * Mark welcome message as shown for this session
   * This prevents the welcome message from showing again until a new session
   */
  markWelcomeMessageShown(): void {
    this.welcomeMessageShownSubject.next(true);
    this.saveToSessionStorage(this.SESSION_KEY_WELCOME_SHOWN, true);
  }

  /**
   * Check if welcome message should be shown (completed but not yet shown this session)
   */
  shouldShowWelcomeMessage(): boolean {
    return this.onboardingCompletedSubject.value && !this.welcomeMessageShownSubject.value;
  }

  /**
   * Get onboarding checklist
   * Fetches from API if organization ID is set, otherwise returns cached data
   */
  getChecklist(): Observable<OnboardingChecklistItem[]> {
    return this.checklist$;
  }

  /**
   * Fetch progress from API and update local state
   * Should be called on login or when organization context changes
   */
  async fetchProgressFromApi(): Promise<void> {
    if (!this.getOrganizationId()) {
      console.warn('No organization ID set, cannot fetch onboarding progress from API');
      return;
    }

    try {
      const response = await firstValueFrom(
        this.http.get<OwnerOnboardingProgressResponse>(
          `${this.apiUrl}/organizations/${this.getOrganizationId()}/owner-onboarding/progress`
        ).pipe(
          catchError(error => {
            console.error('Error fetching onboarding progress from API:', error);
            return of({ success: false as const, data: undefined, error: 'API error' });
          })
        )
      );

      if (response.success && response.data) {
        // Update local state with API data
        this.checklistSubject.next(response.data.checklist);
        this.saveToStorage(this.STORAGE_KEY_CHECKLIST, response.data.checklist);

        this.onboardingDismissedSubject.next(response.data.dismissed);
        this.saveToStorage(this.STORAGE_KEY_DISMISSED, response.data.dismissed);

        const isCompleted = response.data.completedAt !== null;
        this.onboardingCompletedSubject.next(isCompleted);
        this.saveToStorage(this.STORAGE_KEY_COMPLETED, isCompleted);
      }
    } catch (error) {
      console.error('Error in fetchProgressFromApi:', error);
    }
  }

  /**
   * Sync progress with API - checks actual data state and updates
   */
  async syncWithApi(): Promise<void> {
    if (!this.getOrganizationId()) {
      console.warn('No organization ID set, cannot sync onboarding progress');
      return;
    }

    try {
      const response = await firstValueFrom(
        this.http.post<{ success: boolean; data?: { message: string; itemsCompleted: string[] } }>(
          `${this.apiUrl}/organizations/${this.getOrganizationId()}/owner-onboarding/sync`,
          {}
        ).pipe(
          catchError(error => {
            console.error('Error syncing onboarding progress:', error);
            return of({ success: false });
          })
        )
      );

      if (response.success) {
        // Refresh progress from API after sync
        await this.fetchProgressFromApi();
      }
    } catch (error) {
      console.error('Error in syncWithApi:', error);
    }
  }

  /**
   * Mark a checklist item as completed
   */
  completeItem(itemKey: string): Observable<void> {
    if (!this.getOrganizationId()) {
      console.warn('No organization ID set, falling back to localStorage only');
      return this.completeItemLocal(itemKey);
    }

    // Call API to complete item
    return this.http.post<{ success: boolean; data?: { message: string; allRequiredComplete: boolean } }>(
      `${this.apiUrl}/organizations/${this.getOrganizationId()}/owner-onboarding/items/${itemKey}/complete`,
      {}
    ).pipe(
      tap(response => {
        if (response.success) {
          // Update local state
          const currentChecklist = this.checklistSubject.value;
          const updatedChecklist = currentChecklist.map(item => {
            if (item.itemKey === itemKey) {
              return {
                ...item,
                isCompleted: true,
                completedAt: new Date()
              };
            }
            return item;
          });

          this.checklistSubject.next(updatedChecklist);
          this.saveToStorage(this.STORAGE_KEY_CHECKLIST, updatedChecklist);

          // Check if all required complete
          if (response.data?.allRequiredComplete) {
            this.onboardingCompletedSubject.next(true);
            this.saveToStorage(this.STORAGE_KEY_COMPLETED, true);
          }
        }
      }),
      map(() => void 0),
      catchError(error => {
        console.error('Error completing item via API, falling back to localStorage:', error);
        return this.completeItemLocal(itemKey);
      })
    );
  }

  /**
   * Complete item locally (fallback when API unavailable)
   */
  private completeItemLocal(itemKey: string): Observable<void> {
    const currentChecklist = this.checklistSubject.value;
    const updatedChecklist = currentChecklist.map(item => {
      if (item.itemKey === itemKey) {
        return {
          ...item,
          isCompleted: true,
          completedAt: new Date()
        };
      }
      return item;
    });

    this.checklistSubject.next(updatedChecklist);
    this.saveToStorage(this.STORAGE_KEY_CHECKLIST, updatedChecklist);
    this.checkOnboardingComplete(updatedChecklist);

    return of(void 0);
  }

  /**
   * Mark entire onboarding as completed
   */
  completeOnboarding(): Observable<void> {
    this.onboardingCompletedSubject.next(true);
    this.saveToStorage(this.STORAGE_KEY_COMPLETED, true);
    return of(void 0);
  }

  /**
   * Dismiss onboarding checklist (hide it)
   */
  dismissOnboarding(): Observable<void> {
    if (!this.getOrganizationId()) {
      console.warn('No organization ID set, falling back to localStorage only');
      this.onboardingDismissedSubject.next(true);
      this.saveToStorage(this.STORAGE_KEY_DISMISSED, true);
      return of(void 0);
    }

    // Call API to dismiss
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/organizations/${this.getOrganizationId()}/owner-onboarding/dismiss`,
      {}
    ).pipe(
      tap(response => {
        if (response.success) {
          this.onboardingDismissedSubject.next(true);
          this.saveToStorage(this.STORAGE_KEY_DISMISSED, true);
        }
      }),
      map(() => void 0),
      catchError(error => {
        console.error('Error dismissing via API, falling back to localStorage:', error);
        this.onboardingDismissedSubject.next(true);
        this.saveToStorage(this.STORAGE_KEY_DISMISSED, true);
        return of(void 0);
      })
    );
  }

  /**
   * Re-show dismissed onboarding checklist
   */
  showOnboarding(): Observable<void> {
    if (!this.getOrganizationId()) {
      console.warn('No organization ID set, falling back to localStorage only');
      this.onboardingDismissedSubject.next(false);
      this.saveToStorage(this.STORAGE_KEY_DISMISSED, false);
      return of(void 0);
    }

    // Call API to show
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/organizations/${this.getOrganizationId()}/owner-onboarding/show`,
      {}
    ).pipe(
      tap(response => {
        if (response.success) {
          this.onboardingDismissedSubject.next(false);
          this.saveToStorage(this.STORAGE_KEY_DISMISSED, false);
        }
      }),
      map(() => void 0),
      catchError(error => {
        console.error('Error showing via API, falling back to localStorage:', error);
        this.onboardingDismissedSubject.next(false);
        this.saveToStorage(this.STORAGE_KEY_DISMISSED, false);
        return of(void 0);
      })
    );
  }

  /**
   * Check if all required items are completed
   */
  private checkOnboardingComplete(checklist: OnboardingChecklistItem[]): void {
    const allRequiredCompleted = checklist
      .filter(item => item.isRequired)
      .every(item => item.isCompleted);

    if (allRequiredCompleted && !this.onboardingCompletedSubject.value) {
      this.completeOnboarding().subscribe();
    }
  }

  /**
   * Get completion percentage
   */
  getCompletionPercentage(checklist: OnboardingChecklistItem[]): number {
    const visibleItems = checklist.filter(item => item.isVisible);
    if (visibleItems.length === 0) return 100;

    const completedItems = visibleItems.filter(item => item.isCompleted).length;
    return Math.round((completedItems / visibleItems.length) * 100);
  }

  /**
   * Get completed count
   */
  getCompletedCount(checklist: OnboardingChecklistItem[]): { completed: number; total: number } {
    const visibleItems = checklist.filter(item => item.isVisible);
    const completedItems = visibleItems.filter(item => item.isCompleted).length;

    return {
      completed: completedItems,
      total: visibleItems.length
    };
  }

  /**
   * Reset onboarding state (for new user signup or fresh login)
   * Forces the Getting Started checklist to show on dashboard
   */
  resetOnboarding(): Observable<void> {
    // Reset to default state
    this.checklistSubject.next(this.defaultChecklist);
    this.onboardingDismissedSubject.next(false);
    this.onboardingCompletedSubject.next(false);

    // Clear localStorage
    this.saveToStorage(this.STORAGE_KEY_CHECKLIST, this.defaultChecklist);
    this.saveToStorage(this.STORAGE_KEY_DISMISSED, false);
    this.saveToStorage(this.STORAGE_KEY_COMPLETED, false);

    return of(void 0);
  }

  /**
   * Check if user is a new user (no onboarding state in localStorage)
   */
  isNewUser(): boolean {
    return !localStorage.getItem(this.STORAGE_KEY_DISMISSED) &&
           !localStorage.getItem(this.STORAGE_KEY_COMPLETED);
  }

  /**
   * Sync owner onboarding checklist with actual data state
   * Checks organization, certifications, invitations, customers
   *
   * @deprecated Use syncWithApi() instead for database-backed sync
   */
  async syncWithActualState(): Promise<void> {
    // If we have an organization ID, use the API sync
    if (this.getOrganizationId()) {
      await this.syncWithApi();
      return;
    }

    // Legacy localStorage-based sync (fallback)
    try {
      const currentChecklist = [...this.checklistSubject.value];
      let hasChanges = false;

      // Check if organization exists
      try {
        const orgResponse = await firstValueFrom(
          this.http.get<any>(`${this.apiUrl}/organizations/mine`)
        );
        if (orgResponse.success && orgResponse.data) {
          // Set the organization ID in context service for future API calls
          this.organizationContext.setCurrentOrganization(orgResponse.data.id);

          const createCompanyItem = currentChecklist.find(i => i.itemKey === 'create_company');
          if (createCompanyItem && !createCompanyItem.isCompleted) {
            createCompanyItem.isCompleted = true;
            createCompanyItem.completedAt = new Date();
            hasChanges = true;
          }

          // Now try to sync with API since we have the org ID
          await this.syncWithApi();
          return;
        }
      } catch (error) {
        // Organization doesn't exist yet - that's ok
      }

      // Check if organization certifications exist
      try {
        const orgResponse = await firstValueFrom(
          this.http.get<any>(`${this.apiUrl}/organizations/mine`)
        );
        if (orgResponse.success && orgResponse.data?.id) {
          const certsResponse = await firstValueFrom(
            this.http.get<any>(`${this.apiUrl}/certifications/organization/${orgResponse.data.id}`)
          );
          if (certsResponse.certifications && certsResponse.certifications.length > 0) {
            const uploadCertsItem = currentChecklist.find(i => i.itemKey === 'upload_certifications');
            if (uploadCertsItem && !uploadCertsItem.isCompleted) {
              uploadCertsItem.isCompleted = true;
              uploadCertsItem.completedAt = new Date();
              hasChanges = true;
            }
          }
        }
      } catch (error) {
        // No certifications yet - that's ok
      }

      // Check if invitations have been sent
      try {
        const orgResponse = await firstValueFrom(
          this.http.get<any>(`${this.apiUrl}/organizations/mine`)
        );
        if (orgResponse.success && orgResponse.data?.id) {
          const invitesResponse = await firstValueFrom(
            this.http.get<any>(`${this.apiUrl}/organizations/${orgResponse.data.id}/invitations`)
          );
          if (invitesResponse.success && invitesResponse.data && invitesResponse.data.length > 0) {
            const invitePilotsItem = currentChecklist.find(i => i.itemKey === 'invite_pilots');
            if (invitePilotsItem && !invitePilotsItem.isCompleted) {
              invitePilotsItem.isCompleted = true;
              invitePilotsItem.completedAt = new Date();
              hasChanges = true;
            }
          }
        }
      } catch (error) {
        // No invitations yet - that's ok
      }

      // Check if customers have been added
      try {
        const customersResponse = await firstValueFrom(
          this.http.get<any>(`${this.apiUrl}/customers`)
        );
        if (customersResponse.success && customersResponse.data && customersResponse.data.length > 0) {
          const addCustomersItem = currentChecklist.find(i => i.itemKey === 'add_customers');
          if (addCustomersItem && !addCustomersItem.isCompleted) {
            addCustomersItem.isCompleted = true;
            addCustomersItem.completedAt = new Date();
            hasChanges = true;
          }
        }
      } catch (error) {
        // No customers yet - that's ok
      }

      // Save updated checklist if there were changes
      if (hasChanges) {
        this.checklistSubject.next(currentChecklist);
        this.saveToStorage(this.STORAGE_KEY_CHECKLIST, currentChecklist);
        this.checkOnboardingComplete(currentChecklist);
      }
    } catch (error) {
      console.error('Error syncing onboarding state:', error);
    }
  }
}
